import * as Sentry from "@sentry/react";

const SENSITIVE_KEYS = new Set([
  "password", "access_token", "refresh_token",
  "totp_secret", "backup_codes", "code", "otp_token",
  "x-csrf-token", "authorization", "cookie",
]);

function redact<T>(obj: T): T {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((x) => redact(x)) as unknown as T;
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    result[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? "***" : redact(v);
  }
  return result as T;
}

/** SENTRY_DSN env(VITE_SENTRY_DSN) 있으면 init, 없으면 graceful no-op. */
export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    return false;
  }
  try {
    Sentry.init({
      dsn,
      environment: (import.meta.env.MODE ?? "development"),
      release: `argos-acc-frontend@${import.meta.env.VITE_VERSION ?? "0.1.0"}`,
      tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_RATE ?? "0.1"),
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.5,
      sendDefaultPii: false,
      beforeSend(event) {
        if (event.request) {
          if (event.request.headers) {
            for (const k of Object.keys(event.request.headers)) {
              if (SENSITIVE_KEYS.has(k.toLowerCase())) {
                (event.request.headers as Record<string, string>)[k] = "***";
              }
            }
          }
          if (event.request.data) event.request.data = redact(event.request.data);
        }
        if (event.extra) event.extra = redact(event.extra);
        return event;
      },
    });
    return true;
  } catch {
    return false;
  }
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
